import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { buttonProps } from '@components/Buttons/ButtonConfig';
import TooltipTsoPq from '@components/Tooltip/TooltipTsoPq';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, ButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { cloneElement, forwardRef, isValidElement, ReactNode } from 'react';

type StyledButtonProps = ButtonProps & {
  buttonType: ButtonType;
  sizeVariant: SizeVariant;
  customColor?: string;
};

type IconButtonTsoPqProps = StyledButtonProps & {
  tooltip?: string;
  icon?: IconProp;
  customIcon?: ReactNode;
  multilineTooltip?: boolean;
  tooltipMinWidth?: string;
};

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => !['sizeVariant', 'buttonType'].includes(prop as string),
})<StyledButtonProps>(({ sizeVariant, buttonType, disabled, customColor }) => {
  const isCustomTransparent = buttonType === 'transparentWithCustomColor';

  let baseBorder: string;
  if (isCustomTransparent) {
    baseBorder = '1px solid transparent';
  } else if (buttonProps.border[buttonType]) {
    baseBorder = `1px solid ${buttonProps.border[buttonType].normal}`;
  } else {
    baseBorder = 'none';
  }

  let hoverBorder: string;
  if (isCustomTransparent) {
    hoverBorder = `1px solid ${customColor}`;
  } else if (buttonProps.border[buttonType]) {
    hoverBorder = `1px solid ${buttonProps.border[buttonType].hover}`;
  } else {
    hoverBorder = 'none';
  }

  let activeBorder: string;
  if (isCustomTransparent) {
    activeBorder = `1px solid ${customColor}`;
  } else if (buttonProps.border[buttonType]) {
    activeBorder = `1px solid ${buttonProps.border[buttonType].hover}`;
  } else {
    activeBorder = 'none';
  }

  return {
    minHeight: buttonProps.iconButton.size[sizeVariant],
    minWidth: buttonProps.iconButton.size[sizeVariant],
    height: buttonProps.iconButton.size[sizeVariant],
    width: buttonProps.iconButton.size[sizeVariant],
    display: 'flex',
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    padding: buttonProps.iconButton.padding[sizeVariant],
    background: isCustomTransparent ? 'transparent' : buttonProps.background[buttonType],
    color: isCustomTransparent
      ? (customColor ?? tsoPqTheme.palette.customTextColor.color_font_ft_text)
      : buttonProps.color[buttonType],
    border: baseBorder,
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,

    '&:hover': {
      background: isCustomTransparent ? 'transparent' : buttonProps.hover[buttonType],
      border: hoverBorder,
    },

    '&:active': {
      background: isCustomTransparent ? 'transparent' : buttonProps.active[buttonType],
      border: activeBorder,
    },

    '&:focus, &:hover, &:active': {
      boxShadow: 'none',
    },
  };
});

const IconButtonTsoPq = forwardRef(
  (
    {
      buttonType,
      sizeVariant,
      tooltip,
      multilineTooltip = false,
      tooltipMinWidth,
      icon,
      customIcon,
      disabled,
      ...props
    }: IconButtonTsoPqProps,
    ref
  ) => {
    const iconSize = buttonProps.iconButton.iconSize[sizeVariant ?? 'm'];

    const renderedCustomIcon =
      customIcon && isValidElement(customIcon) ? cloneElement(customIcon, { fontSize: 'inherit' }) : customIcon;

    const customIconContent = customIcon ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: iconSize,
          lineHeight: 1,
          color: 'inherit',
        }}
      >
        {renderedCustomIcon}
      </Box>
    ) : null;

    const fontAwesomeIcon = icon ? (
      <FontAwesomeIcon icon={icon} fontSize={iconSize} />
    ) : null;

    const iconContent = customIconContent ?? fontAwesomeIcon;

    const buttonContent = (
      <StyledButton
        buttonType={buttonType}
        sizeVariant={sizeVariant ?? 'm'}
        disableRipple
        disabled={disabled}
        {...props}
      >
        {iconContent}
      </StyledButton>
    );

    return (
      <TooltipTsoPq title={tooltip} multiline={multilineTooltip} customWidth={tooltipMinWidth}>
        <Box sx={{ display: 'flex' }}>{buttonContent}</Box>
      </TooltipTsoPq>
    );
  }
);

export default IconButtonTsoPq;
